import React, { useState } from 'react';
import { MuiDrawerMobile, MuiChip, MuiSwitch, MuiAccordion, MuiDialogue } from '@ace/ace-ui-web';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import styles from './StoreBanner.module.css';
import { useClickHandler } from '../../utils/usageMetricsLogger';
import { useBuildDynamicUrl } from '../../hooks/useDynamicUrl';
import { isMobileDevice, getEffectiveStoreNumber } from '../../utils/appUtils';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  selectPeerGroupDescription,
  selectPeerGroupLoadedMode,
} from '../../redux/PerformanceReducer/Ecommerce/ecommSalesPerfSlice';
import { selectUserDetails } from '../../redux/UserReducer/userSlice';
import {
  downloadScorecard,
  selectIsScorecardExportLoading,
} from '../../redux/HeaderReducer/scorecardExportSlice';
import { RADRequest } from '../../models/common/RADRequest';
// Icons
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import StoreIcon from '@mui/icons-material/Store';
import DownloadIcon from '@mui/icons-material/Download';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PrintIcon from '@mui/icons-material/Print';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Store } from '../../models/UserModel';
import lang from '../../languages/en.json';
import { env } from '../../env';

interface StoreBannerMobileProps {
  currentStore?: Store | null;
  userChainListLength?: number;
  isOpen: boolean;
  onClose: () => void;
  higherGroundStatus?: string;
  higherGroundLogoImg?: string;
  onPerformanceClick?: () => void;
  onOperationsClick?: () => void;
  onADWReportClick?: () => void;
  onADWDashboardClick?: () => void;
  onMultiStoreToggle?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onPrintClick?: (items: string[]) => void;
  /** When true, disables the print accordion while data is still loading */
  isPrintDisabled?: boolean;
  isMultiStoreEnabled?: boolean;
  onProgressReportClick?: () => void;
  onSummaryClick?: () => void;
}
const StoreBannerMobile = ({
  currentStore,
  userChainListLength = 0,
  isOpen,
  onClose,
  higherGroundStatus = 'N/A',
  higherGroundLogoImg = '/images/hg_logo.svg',
  onPerformanceClick,
  onOperationsClick,
  onADWReportClick,
  onADWDashboardClick,
  onMultiStoreToggle,
  onPrintClick,
  isPrintDisabled = false,
  isMultiStoreEnabled = false,
  onProgressReportClick,
  onSummaryClick,
}: StoreBannerMobileProps) => {
  const handleAndLogClick = useClickHandler('Header');
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(selectUserDetails);
  const isScorecardLoading = useAppSelector(selectIsScorecardExportLoading);
  const peerGroupDescription = useAppSelector(selectPeerGroupDescription);
  const peerGroupLoadedMode = useAppSelector(selectPeerGroupLoadedMode);
  // Only show the loaded name when it matches the active mode; otherwise a dash. Avoids a stale
  // name from the previous mode flashing during a single↔multi switch.
  const peerGroupName =
    peerGroupLoadedMode === isMultiStoreEnabled && peerGroupDescription
      ? peerGroupDescription
      : '-';
  const [isUserGuideOpen, setIsUserGuideOpen] = useState<boolean>(false);
  const [isScorecardDialogOpen, setIsScorecardDialogOpen] = useState<boolean>(false);
  const [scorecardDialogTitle, setScorecardDialogTitle] = useState<string>('');

  const baseUrlType = 'RETAILER';
  const buildDynamicUrl = useBuildDynamicUrl();
  const ADW_PILOT_PHASE = env.ADW_DASHBOARD_PILOT_MODE ?? false; // Default to false if not defined
  const showADWDashboard = !ADW_PILOT_PHASE || new URLSearchParams(window.location.search).get('showADWDashboard') === 'true';    //showing ADW dashboard in pilot phase when you pass a URL param



  // User Guide dialog text
  const userGuideContent = lang.storeBanner.userGuidePopupContent;

  // Handler to open User Guide dialog
  const handleUserGuideClick = (): void => {
    setIsUserGuideOpen(true);
  };

  // Handler to close User Guide dialog
  const handleCloseUserGuide = (): void => {
    setIsUserGuideOpen(false);
  };

  // Handler to download the selected Scorecard report. Mirrors the desktop flow in
  // Header.tsx: scorecards download for all users; the dialog below only surfaces an
  // error when the store/chain context is missing or the download fails.
  const handleScorecardItemClick = async (itemName: string): Promise<void> => {
    setScorecardDialogTitle(itemName);

    if (isScorecardLoading) {
      return;
    }

    // Multi-Store reports use the parent/effective store; Peer Group reports use the
    // user's currently selected store. Match the mode sent to getEffectiveStoreNumber.
    const isMultiStoreMode = itemName === 'Multi-store Report';
    const chainStores = userDetails?.userChainList?.map(store => store.storeNumber) ?? [];
    const effectiveStore = userDetails
      ? getEffectiveStoreNumber(userDetails, isMultiStoreMode)
      : '';

    // Peer Group scorecards are valid for single-store users (empty chain list),
    // so only require a populated chain when running in Multi-Store mode.
    if (!effectiveStore || (isMultiStoreMode && chainStores.length === 0)) {
      setIsScorecardDialogOpen(true);
      return;
    }

    const requestBody: RADRequest = {
      storeNumber: effectiveStore,
      isMultiStoreMode,
      storeNumbers: chainStores,
    };

    try {
      await dispatch(downloadScorecard(requestBody)).unwrap();
    } catch {
      setIsScorecardDialogOpen(true);
    }
  };

  // Handler to close Scorecard dialog
  const handleCloseScorecardDialog = (): void => {
    setIsScorecardDialogOpen(false);
  };

  // Common styling constants
  const COMMON_BUTTON_SX = {
    height: '40px',
    px: '20px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  };

  const COMMON_TEXT_PRIMARY_SX = {
    fontFamily: 'Roboto',
    fontWeight: 400,
    color: '#fff',
  };

  const ACCORDION_WRAPPER_SX = {
    minHeight: '40px',
    px: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  };

  const COMMON_ACCORDION_SUMMARY_SX = {
    px: 0,
    '& .MuiAccordionSummary-content': {
      margin: '9px 0',
      flex: 0,
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: '9px 0',
      flex: 0,
    },
  };

  // Helper function for menu item with link
  const renderMenuItem = (
    icon: React.ReactNode,
    text: string,
    href?: string,
    additionalSx = {},
    target?: string,
    onClick?: () => void,
  ) => (
    <ListItem disablePadding>
      <ListItemButton
        {...(href ? { href, target, rel: 'noopener noreferrer' } : { component: 'div' })}
        onClick={onClick}
        sx={{ ...COMMON_BUTTON_SX, ...additionalSx }}>
        <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>{icon}</ListItemIcon>
        <ListItemText
          primary={text}
          sx={{ flex: 'none' }}
          slotProps={{
            primary: {
              sx: COMMON_TEXT_PRIMARY_SX,
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  // Helper function for accordion menu item
  const renderAccordionMenuItem = (
    icon: React.ReactNode,
    title: string,
    items: Array<{
      text: string;
      url?: string;
      openInNewTab?: boolean;
      onClick?: () => void;
    }>,
    detailsPadding = '0 16px 0 11px',
  ) => (
    <ListItem disablePadding>
      <Box sx={ACCORDION_WRAPPER_SX}>
        <ListItemIcon sx={{ minWidth: 32, color: '#fff', mt: '9px' }}>{icon}</ListItemIcon>
        <MuiAccordion
          backgroundColor="transparent"
          elevation={0}
          indentContent
          items={[
            {
              content: items.map(item => ({
                openInNewTab: item.openInNewTab,
                text: item.text,
                url: item.url,
                onClick: item.onClick,
              })),
              title,
            },
          ]}
          textColor="#fff"
          accordionSummarySx={COMMON_ACCORDION_SUMMARY_SX}
          accordionDetailsSx={{
            padding: detailsPadding,
          }}
        />
      </Box>
    </ListItem>
  );
  const isActualMobileDevice: boolean = isMobileDevice();
  return (
    <MuiDrawerMobile
      storeName={currentStore?.storeName ?? ''}
      storeNumber={currentStore?.storeNumber ?? ''}
      city={currentStore?.storeCity ?? ''}
      state={currentStore?.storeState ?? ''}
      open={isOpen}
      onClose={onClose}>
      {/* Peer Group / Multi-Store Group name — rendered directly under the city/state, outside the menu <ul>. Tooltip shows the actual name. */}
      <Box sx={{ pl: '20px', pr: '16px' }}>
        <Tooltip
          title={peerGroupName}
          arrow
          enterTouchDelay={0}
          leaveTouchDelay={6000}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: '#fff', mr: 1 }}
            />
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 400,
                color: '#fff',
              }}>
              {isMultiStoreEnabled
                ? lang.storeBanner.yourMultiStoreGroup
                : lang.storeBanner.yourStorePeerGroup}
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      <List sx={{ p: 0, mt: 3 }}>
        {/* Higher Ground Status with badge */}
        <ListItem
          disablePadding
          sx={{
            flexDirection: 'column',
            alignItems: 'flex-start',
            px: '20px',
            py: 1,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>
              <img
                src={higherGroundLogoImg}
                alt="Higher Ground"
                width="24"
                height="16"
                className={styles.mobileDrawerHigherGroundLogo}
              />
            </ListItemIcon>
            <ListItemText
              primary="Higher Ground Status"
              sx={{ flex: 'none' }}
              slotProps={{
                primary: {
                  sx: {
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontStyle: 'italic',
                    color: '#fff',
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ pl: '33px', paddingBottom: '4px' }}>
            <MuiChip
              colors="red"
              label={higherGroundStatus}
              variant="filled"
              size="small"
            />
          </Box>
        </ListItem>

        {/* Progress Report */}
        {!isActualMobileDevice &&
          renderMenuItem(<DownloadIcon />, 'Progress Report', undefined, {}, undefined, () =>
            handleAndLogClick('Higher Ground Progress Report', {
              callback: () => onProgressReportClick?.(),
            }),
          )}

        {/* My Business Plan */}
        {!isActualMobileDevice &&
          renderMenuItem(
            <TrendingUpIcon />,
            lang.storeBanner.myBusinessPlan,
            undefined,
            {},
            undefined,
            () => {
              const url = buildDynamicUrl(baseUrlType, 'RGGP');
              handleAndLogClick('My Business Plan', {
                url,
              });
            },
          )}

        {/* User Guide */}
        {renderMenuItem(<HelpOutlineIcon />, 'User Guide', undefined, {}, undefined, () => {
          if (isActualMobileDevice) {
            // Mobile devices: Show dialog instead of PDF URL
            handleAndLogClick('User Guide', { callback: handleUserGuideClick });
          } else {
            // Desktop: Navigate to help page
            const userGuideUrl = buildDynamicUrl('ACENET', 'USER_GUIDE');
            handleAndLogClick('User Guide', {
              url: userGuideUrl,
            });
          }
        })}

        {/* Performance */}
        {renderMenuItem(
          <StackedBarChartIcon />,
          'Performance',
          undefined,
          {
            mt: '40px',
          },
          undefined,
          () => {
            if (onPerformanceClick) {
              onPerformanceClick();
            }
            onClose();
          },
        )}

        {/* Operations */}
        {renderMenuItem(<SettingsIcon />, 'Operations', undefined, {}, undefined, () => {
          if (onOperationsClick) {
            onOperationsClick();
          }
          onClose();
        })}

        {/* Scorecard */}
        {renderAccordionMenuItem(<StarOutlineIcon />, 'Scorecard', [
          {
            text: 'Peer Group Report',
            onClick: () =>
              handleAndLogClick('Scorecard - Peer Group Report', {
                callback: () => handleScorecardItemClick('Peer Group Report'),
              }),
          },
          {
            text: 'Multi-store Report',
            onClick: () =>
              handleAndLogClick('Scorecard - Multi-store Report', {
                callback: () => handleScorecardItemClick('Multi-store Report'),
              }),
          },
        ])}

        {/* Reporting */}
        {!isActualMobileDevice && showADWDashboard && 
          renderAccordionMenuItem(<AssessmentIcon />, lang.header.reportingPascalCase, [
            {
              text: lang.header.adwReports,
              // The parent's onADWReportClick handler already logs a ClickEvent
              // with the resolved URL, so don't wrap in handleAndLogClick here
              // or the event will be logged twice per selection.
              onClick: () => {
                onADWReportClick?.();
                onClose();
              },
            },
            {
              text: lang.header.adwDashboards,
              onClick: () => {
                onADWDashboardClick?.();
                onClose();
              },
            }
          ])
        }
        {!isActualMobileDevice && !showADWDashboard &&    //keeping this separate to ensure code removing is easy post pilot phase
          renderMenuItem(
            <AssessmentIcon />,
            'ADW Report',
            undefined,
            { mt: '40px' },
            undefined,
            () => {
              if (onADWReportClick) {
                onADWReportClick();
              }
              onClose();
            },
          )}

        {/* Summary */}
        {!isActualMobileDevice &&
          renderMenuItem(<DownloadIcon />, 'Summary', undefined, {}, undefined, () =>
            handleAndLogClick('Summary Download', {
              callback: () => {
                onClose();
                onSummaryClick?.();
              },
            }),
          )}

        {/* Print - hidden on actual mobile devices in prod, visible on narrow desktop browsers */}
        {(env.SHOW_MOBILE_PRINT || !isMobileDevice()) && (
          <Box
            sx={{
              ...(isPrintDisabled && {
                pointerEvents: 'none',
                opacity: 0.5,
                cursor: 'not-allowed',
              }),
            }}>
            {renderAccordionMenuItem(
              <PrintIcon />,
              'Print',
              [
                {
                  openInNewTab: true,
                  text: 'Operations',
                  onClick: () => {
                    if (onPrintClick) {
                      onPrintClick(['Operations']);
                    }
                    onClose();
                  },
                },
                {
                  openInNewTab: true,
                  text: 'Performance',
                  onClick: () => {
                    if (onPrintClick) {
                      onPrintClick(['Performance']);
                    }
                    onClose();
                  },
                },
              ],
              '0 16px 20px 11px',
            )}
          </Box>
        )}

        {/* Multi-store view with toggle */}
        {userChainListLength > 1 && (
          <ListItem disablePadding>
            <ListItemButton
              component="div"
              sx={{
                ...COMMON_BUTTON_SX,
                cursor: 'default',
              }}>
              <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>
                <StoreIcon />
              </ListItemIcon>
              <ListItemText
                primary="Multi-store view"
                sx={{ flex: 'none' }}
                slotProps={{
                  primary: {
                    sx: COMMON_TEXT_PRIMARY_SX,
                  },
                }}
              />
              <MuiSwitch
                key={String(isMultiStoreEnabled)}
                color="primary"
                size="medium"
                defaultChecked={isMultiStoreEnabled}
                onChange={(event, checked) => {
                  if (onMultiStoreToggle) {
                    onMultiStoreToggle(event, checked);
                  }
                  onClose();
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      {/* User Guide Dialog */}
      <MuiDialogue
        open={isUserGuideOpen}
        hideTriggerButton
        dialogTitle={lang.storeBanner.userGuide}
        onClose={handleCloseUserGuide}
        dialogContent={
          <Box>
            <Typography
              variant="body1"
              gutterBottom>
              {userGuideContent}
            </Typography>
          </Box>
        }
      />

      {/* Scorecard Dialog — shown only when a download cannot be completed */}
      <MuiDialogue
        open={isScorecardDialogOpen}
        hideTriggerButton
        dialogTitle={scorecardDialogTitle}
        onClose={handleCloseScorecardDialog}
        dialogContent={
          <Box>
            <Typography
              variant="body1"
              gutterBottom>
              {lang.pageContent.dataUnavailableError.replace('{section}', scorecardDialogTitle)}
            </Typography>
          </Box>
        }
      />

    </MuiDrawerMobile>
  );
};

export default StoreBannerMobile;
